import * as React from 'react';
import moment from 'moment';
import { withStyles, Typography, Button } from '@material-ui/core';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import Link from 'next/link';
import { connect } from 'react-redux';
import Disclaimer from './Disclaimer';
import PriceHistory from './PriceHistory';
import ToriiLicense from '../../utility/ToriiLicense';
import {
  percentage,
  normalizePrice,
  formatAgentDetails,
  replaceSplApostrophe,
} from '../../../lib/helpers';
import styles from './styles';
import { getPricePointerData, getTrends } from '../../../actions/listing';
import { isMobile } from '../../../lib/getDeviceSize';
import { capitalize } from 'lodash';

interface Props {
  classes: any;
  getPricePointerData: Function;
  getTrends: Function;
  listing: any;
  pricePoints: any;
  trends: any;
  showInModal?: boolean;
}

interface State {
  aboutExpanded: boolean;
}

export class Details extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      aboutExpanded: false,
    };
    this.props.getTrends(
      this.props.listing.zipCode,
      this.props.listing.noBedrooms,
    );
    this.props.getPricePointerData(this.props.listing.zipCode);
  }

  updateExpand = () => {
    this.setState({
      aboutExpanded: !this.state.aboutExpanded,
    });
  };

  _renderTitle = (title: string) => (
    <Typography className={this.props.classes.detailsTitle}>{title}</Typography>
  );

  _renderList = (label: string, value: any) => (
    <div
      className={this.props.classes.listItem}
      style={{ width: this.props.showInModal ? 230 : '' }}
    >
      <Typography className={this.props.classes.listLabel}>{label}</Typography>
      <Typography className={this.props.classes.listValue}>
        {value ? value : '-'}
      </Typography>
    </div>
  );

  _renderAbout = () => {
    const { aboutExpanded } = this.state;
    const { classes, listing } = this.props;
    const decodedRemarks = replaceSplApostrophe(listing.remarks);
    const remarksToBeDisplayed =
      decodedRemarks.length > 300
        ? !aboutExpanded
          ? `${decodedRemarks.slice(0, 300)}...`
          : decodedRemarks
        : decodedRemarks;
    return (
      <div className={`${classes.marginContainer}`}>
        {this._renderTitle('About This Property')}
        <Typography className={classes.aboutText}>
          {remarksToBeDisplayed}
        </Typography>
        {listing.remarks.length > 300 && (
          <Button onClick={this.updateExpand} className={classes.expandButton}>
            {!aboutExpanded ? <ExpandMore /> : <ExpandLess />}
            <Typography className={classes.expandButtonText}>
              {!aboutExpanded ? 'Read More' : 'Read Less'}
            </Typography>
          </Button>
        )}
      </div>
    );
  };

  _renderInsights = () => {
    const { classes, listing, trends } = this.props;
    let trendsObj: any = {};
    if (trends && Object.keys(trends).length !== 0) {
      [trendsObj] = trends;
    }
    let rent = trendsObj.averageRent * 12;
    if (parseFloat(listing.totalRent) * 12 > rent) {
      rent = parseFloat(listing.totalRent) * 12;
    }

    const capRate = rent / listing.listPrice;
    const pricePerSf =
      listing.soldPricePerSqft || listing.listPricePerSqft
        ? `$${normalizePrice(
            listing.soldPricePerSqft
              ? listing.soldPricePerSqft
              : listing.listPricePerSqft,
            2,
          )}`
        : null;
    return (
      <div className={classes.marginContainer}>
        {this._renderTitle('Price Insights')}
        <div className="row center-xs" style={{ marginTop: 15 }}>
          <div className={`col-sm-6 ${classes.listItemsContainer}`}>
            {this._renderList(
              'Cap Rate',
              !!capRate ? percentage(capRate) : '-',
            )}
          </div>
          <div className={`col-sm-6 ${classes.listItemsContainer}`}>
            {this._renderList('Price/sf', pricePerSf)}
            {this._renderList(
              'Est. Annual Rent',
              !!rent ? `$${normalizePrice(rent)}` : '-',
            )}
          </div>
        </div>
      </div>
    );
  };

  _renderHomeFacts = () => {
    const { classes, listing } = this.props;
    // Some agents will put an arbitrarily large number for taxes, which breaks the cap rate calc.
    const taxes = !!listing.taxes
      ? listing.taxes > 99999
        ? 0
        : `$${normalizePrice(parseFloat(listing.taxes))}`
      : undefined;
    return (
      <div className={classes.marginContainer}>
        {this._renderTitle('Home Facts')}
        <div className="row center-xs" style={{ marginTop: 15 }}>
          <div className={`col-sm-6 ${classes.listItemsContainer}`}>
            {this._renderList('Days on Torii', listing.marketTime)}
            {this._renderList('Property Type', listing.propType)}
            {this._renderList('Year Built', listing.yearBuilt)}
            {this._renderList(
              'Listed',
              moment(listing.listDate).format('MM/DD/YY'),
            )}
            {this._renderList('Taxes', taxes)}
            {this._renderList(
              'HOA Fee',
              listing.hoaFee ? `$${normalizePrice(listing.hoaFee)}` : '-',
            )}
          </div>
          <div className={`col-sm-6 ${classes.listItemsContainer}`}>
            {this._renderList('Heating', listing.heating)}
            {this._renderList('Cooling', listing.cooling)}
            {this._renderList('Parking', listing.parkingSpaces)}
            {this._renderList('Garage', listing.garageSpaces)}
            {this._renderList(
              'Lot Size',
              listing.lotSize ? listing.lotSize + ' acres' : listing.lotSize,
            )}
            {this._renderList('MLS #', listing.id)}
          </div>
        </div>
      </div>
    );
  };

  _renderAgentDetails = () => {
    const { classes, listing } = this.props;
    if (
      !listing.agentName &&
      !listing.agentMlsId &&
      !listing.brokerName &&
      !listing.brokerMlsNumber
    ) {
      return null;
    }
    const { source } = listing; // bright_mls
    if (source === 'bright_mls') {
      return (
        <div className={classes.marginContainer}>
          {listing.brokerName && (
            <>
              <p className={classes.agentDetails}>
                <strong>
                  Listing Broker: {capitalize(listing.brokerName)}
                </strong>
              </p>
            </>
          )}
          {listing.agentName && (
            <p className={classes.agentDetails}>
              <strong>Listing Agent:</strong> {listing.agentName} (
              {listing.agentMlsId})
            </p>
          )}
        </div>
      );
    }
    return (
      <div className={classes.marginContainer}>
        <p className={classes.agentDetails}>
          Listed by: {formatAgentDetails(listing)}
        </p>
      </div>
    );
  };

  _renderBuyersAgentDetails = () => {
    const { classes, listing } = this.props;
    if (
      !listing.buyerAgentName &&
      !listing.buyerAgentMlsId &&
      !listing.buyerBrokerName &&
      !listing.buyerBrokerMlsNumber
    ) {
      return null;
    }
    return (
      <div className={classes.marginContainer}>
        <p className={classes.agentDetails}>
          Bought with:{' '}
          {formatAgentDetails({
            agentName: listing.buyerAgentName,
            agentMlsId: listing.buyerAgentMlsId,
            brokerName: listing.buyerBrokerName,
            brokerMlsNumber: listing.buyerBrokerMlsNumber,
          })}
        </p>
      </div>
    );
  };

  _renderPriceHistory = (showPriceHistory: boolean) => {
    const { classes, listing } = this.props;
    if (!showPriceHistory) {
      return null;
    }
    return (
      <div className={classes.priceHistoryContainer}>
        <div className={classes.priceHistoryTitle}>
          {this._renderTitle('Price History')}
        </div>
        <PriceHistory
          priceHistory={listing.priceHistory}
          listingMlsId={listing.listingMlsId}
        />
      </div>
    );
  };

  _renderDisclaimer = () => {
    return <Disclaimer listing={this.props.listing} />;
  };

  _renderLicense = () => {
    const { classes } = this.props;
    return (
      <div className={classes.marginContainer}>
        <div className={classes.policyContainer}>
          <Link href="/legal/privacy" prefetch={false}>
            <Typography className={classes.footerText}>
              Privacy Policy
            </Typography>
          </Link>
          <Typography className={classes.footerText}>&nbsp;|&nbsp;</Typography>
          <Link href="/legal/terms" prefetch={false}>
            <Typography className={classes.footerText}>
              Terms of Service
            </Typography>
          </Link>
        </div>
        <ToriiLicense />
      </div>
    );
  };

  _renderSeparator = () => {
    return <div className={this.props.classes.separator} />;
  };

  render() {
    const { listing, showInModal } = this.props;
    const showPriceHistory =
      listing.priceHistory && listing.priceHistory.length > 0;
    return (
      <div
        className={this.props.classes.detailsContainer}
        style={{ width: showInModal ? '90%' : '' }}
      >
        {this._renderAbout()}
        {isMobile() && this._renderSeparator()}
        {this._renderAgentDetails()}
        {this._renderBuyersAgentDetails()}
        {isMobile() && this._renderSeparator()}
        {this._renderInsights()}
        {isMobile() && this._renderSeparator()}
        {this._renderHomeFacts()}
        {isMobile() && this._renderSeparator()}
        {this._renderPriceHistory(showPriceHistory)}
        {!showPriceHistory && isMobile() && this._renderSeparator()}
        {this._renderDisclaimer()}
        {isMobile() && this._renderLicense()}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  pricePoints: state.listings.pricePoints,
  trends: state.listings.trends,
});

const mapDispatchToProps = (dispatch: any) => ({
  getPricePointerData(zipCode: string) {
    dispatch(getPricePointerData(zipCode));
  },
  getTrends(zipCode: string, noBedrooms: number) {
    dispatch(getTrends(zipCode, noBedrooms));
  },
});
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(Details));
